import React from "react";
import { useParams } from "react-router-dom";

import { Loading, Typography } from "ui-library/atoms";
import { ContentBox } from "ui-library/layouts";

import i18n from "core/i18n";
import userContracts from "core/userContracts";
import { Form } from "core/hookForm";
import { useHasRole } from "core/hooks";
import { InPageHookFormLayout } from "containers/inPageForm";
import { Roles } from "enums";
import { ChangeStatus, ChangeSuccessTextType, SignatureType } from "types";

import { FORM_CONTRACT_CANCELLATION } from "../../constants";

import { Step0Introduction, Step1CancellationParams, Step2ReviewComplete } from "./containers";
import { useContractCancellationForm } from "./hooks/useContractCancellationForm";
import { FormStep } from "./types";

export const VehicleCancellationForm = () => {
    const { t } = i18n.useTranslation();
    const { idObject } = useParams();
    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);
    const contract = userContracts.useGetContractById(idObject);

    const {
        form,
        signatureType,
        isLoading,
        isSubmitting,
        isFeasible,
        formStep,
        changeStatus,
        cancellationReasons,
        containsParallelCancellable,
        digitalSignatureFormStatus,
        digitalSignatureOnlineStatus,
        setFormStep,
        onSubmitStep0Validation,
        onMoveToFormStep,
        onSubmitCancellationParams,
        onCompleteCancellation,
    } = useContractCancellationForm();

    const successTextType =
        signatureType === SignatureType.ELECTRONIC && !isAuthenticated
            ? ChangeSuccessTextType.SERVICE_HUB_CHANGE_SIGNATURE_BY_EMAIL_PROCESSED
            : ChangeSuccessTextType.SERVICE_HUB_CHANGE_WITH_SIGNATURE_PROCESSED;

    return (
        <Form form={form} onSubmit={() => {}}>
            <InPageHookFormLayout
                formName={FORM_CONTRACT_CANCELLATION}
                formTitle={t("vehicle.change.contractCancellation.title", {
                    contractNumber: contract.contractNumber,
                })}
                isFormSubmitted={changeStatus === ChangeStatus.COMPLETED}
                changeSuccessTextType={successTextType}
                showFeedbackSurvey
            >
                <Loading loading={isLoading}>
                    {!isFeasible && (
                        <ContentBox mt={3}>
                            <Typography variant={"b"}>{t("vehicle.change.notFeasible.edit")}</Typography>
                        </ContentBox>
                    )}

                    {formStep === FormStep.S0_INTRODUCTION && (
                        <Step0Introduction onSubmitValidation={onSubmitStep0Validation} isSubmitting={isSubmitting} />
                    )}
                    {formStep === FormStep.S1_CANCELLATION_PARAMS && (
                        <Step1CancellationParams
                            containsParallelCancellable={containsParallelCancellable}
                            cancellationReasons={cancellationReasons}
                            onMoveToFormStep={onMoveToFormStep}
                        />
                    )}
                    {formStep === FormStep.S2_REVIEW_SUBMIT && (
                        <Step2ReviewComplete
                            changeStatus={changeStatus}
                            cancellationReasons={cancellationReasons}
                            digitalSignatureFormStatus={digitalSignatureFormStatus}
                            digitalSignatureOnlineStatus={digitalSignatureOnlineStatus}
                            setFormStep={setFormStep}
                            onSubmitCancellationParams={onSubmitCancellationParams}
                            onCompleteCancellation={onCompleteCancellation}
                            isSubmitting={isSubmitting}
                        />
                    )}
                </Loading>
            </InPageHookFormLayout>
        </Form>
    );
};
